<template>
  <b-row>
    <b-col cols="12">
        <p><strong>Recipients</strong><br></p>
        <p class="text-muted">Place every recipient in a new line in the following format:<br/>
          Full Name, Email Address, Tracking Code (optional)</p>
      <b-textarea
        v-model="emailAddressesText"
        placeholder="Mary Doe, mary.joe@reallinks.io, ID1234"
      />
    </b-col>
    <b-col sm="12">
      <b-btn class="mt-3 float-right" @click="input" variant="primary" :disabled="$apollo.loading || !recipients || !recipients.length">
        <template v-if="!$apollo.loading">
          Send Test Message
          <font-awesome-icon class="ml-2" icon="paper-plane"/>
        </template>
        <template v-else>
          <b-spinner small type="grow" variant="light"></b-spinner>
          Validating...
        </template>
      </b-btn>
    </b-col>
    <b-col sm="12">
      <p class="mt-3">
        If the message is not delievered in 30 seconds, please click on the "Send Test Message" button again.
      </p>
    </b-col>
  </b-row>
</template>

<script>
import gql from 'graphql-tag';
import { getShareURL } from '@/lib/share';
import { mapRoot } from '@src/mixins/root';

const demoEmailAddressMap = {
  // Sarah Jane
  'kathleen@demo.reallinks.io': 'sarahjane.grant@reallinks.io',
  // Hedi
  'dean@demo.reallinks.io': 'hedi.ouardani@reallinks.io',
  // Sam
  'nadine@demo.reallinks.io': 'sam.davies@reallinks.io',
};

const UsersQuery = gql`
  query TestSlackMessageUsers(
    $usersWhere: UserWhereManyInput,
    $getJobs: Boolean = false,
    $jobsWhere: JobWhereManyInput,
    $getContentShares: Boolean = false,
    $contentSharesWhere: ContentShareWhereManyInput
  ) {
    company {
      id
      users(where: $usersWhere) {
        id
        emailAddress
        jobs(where: $jobsWhere) @include(if:$getJobs) {
          id
          title
          excerpt
          shareCode
          linkedInSearchURL
        }
        contentShares(where: $contentSharesWhere) @include(if:$getContentShares)  {
          id
          title
          description
          shareCode
        }
      }
    }
  }
`;

const FromUserQuery = gql`
  query TestFromUser(
    $emailAddress: String,
  ) {
    company {
      id
      users(
        where: {
          NOT: { state: null },
          emailAddress: $emailAddress
        }
      ) {
          id
          emailAddress
          fullName
          state {
            microsoft
            slack
            gmail
            emailSignature
          }
      }
    }
  }
`;

const SendTestMessageMutation = gql`
  mutation SendTestMessage($input: String!) {
    sendTestMessage(input: $input)
  }
`;

export default {
  props: {
    body: {
      type: String,
    },
    subject: {
      type: String,
    },
    variables: {
      type: String,
    },
    from: {
      type: String,
    },
    job: {
      type: Object,
    },
    jobs: {
      type: Array,
    },
    contentShare: {
      type: Object,
    },
    contentShares: {
      type: Array,
    },
    collectiveGoal: {
      type: Object,
    },
    isIndividual: {
      type: Boolean,
    },
    channel: {
      type: String,
      default: 'slack',
    }
  },
  data() {
    return {
      emailAddressesText: null,
    };
  },
  apollo: {
    users: {
      query: UsersQuery,
      variables() {
        const { job, jobs, contentShare, contentShares, recipients } = this;
        const usersWhere = {
          OR: recipients.map(({ emailAddress }) => ({ emailAddress }))
        };
        console.log({
          usersWhere,
          getJobs: job || jobs,
          jobsWhere: job ? { id_in: [ job.id ] } : (Array.isArray(jobs) ? { id_in: jobs.map(({ id }) => id) } : null),
          getContentShares: !!(contentShare || contentShares),
          contentSharesWhere: contentShare ? { id_in: [ contentShare.id ] } : (Array.isArray(contentShares) ? { id_in: contentShares.map(({ id }) => id) } : null)
        });
        return {
          usersWhere,
          getJobs: !!(job || jobs),
          jobsWhere: job ? { id_in: [ job.id ] } : (Array.isArray(jobs) ? { id_in: jobs.map(({ id }) => id) } : null),
          getContentShares: !!(contentShare || contentShares),
          contentSharesWhere: contentShare ? { id_in: [ contentShare.id ] } : (Array.isArray(contentShares) ? { id_in: contentShares.map(({ id }) => id) } : null)
        };
      },
      update({ company }) {
        const { users } = company || {};
        return Array.isArray(users) ? users : null;
      },
      error(err) {
        this.$log.error(err);
      },
      errorPolicy: 'all',
      fetchPolicy: 'network-only',
      deep: true,
      skip() {
        const { recipients } = this;
        return !Array.isArray(recipients) || !recipients.length;
      }
    },
    fromUser: {
      query: FromUserQuery,
      variables() {
        return { emailAddress: this.from };
      },
      update({ company }) {
        const { users } = company || {};
        return Array.isArray(users) ? users[0] : null;
      },
      error(err) {
        this.$log.error(err);
      },
      errorPolicy: 'all',
      fetchPolicy: 'network-only',
      deep: true,
      skip() {
        const { from } = this;
        return !(from || '').includes('@');
      }
    },
  },
  computed: {
    ...mapRoot([ 'company' ]),
    recipients() {
      const { emailAddressesText } = this;
      const lines = (emailAddressesText || '').split('\n')
        .filter(l => l && l.includes(',') && l.includes('@'));
      return lines.map(el => {
        const fullName = el.split(',')[0].trim();
        const emailAddress = el.split(',')[1].trim();
        const trackingCode = el.split(',')[2] ? el.split(',')[2].trim() : null;
        const firstName = fullName.split(' ')[0].trim();
        const lastName = (fullName.split(' ')[1] || '').trim();
        return {
          emailAddress,
          trackingCode,
          variables: {
            LEADERBOARD_POSITION: Math.round(Math.random() * 100),
            LEADERBOARD_POINTS: Math.round(Math.random() * 500),
            FIRST_NAME: firstName,
            LAST_NAME: lastName,
            FULL_NAME: fullName,
          }
        };
      });
    }
  },
  methods: {
    async input() {
      const { recipients, users, body, fromUser, job, jobs, contentShare, contentShares, collectiveGoal, channel, company, subject } = this;
      const contentShareText = 'We are hiring. Apply to one of our openings!';
      const jobShareText = 'Do you want to work with me? Checkout the following job opening!';
      let messageType = null;
      let accessToken = null;
      let refreshToken = null;
      switch (channel) {
        case 'slack':
          messageType = 'SLACK';
          accessToken = (fromUser || {}).state.slack.accessToken;
          break;
        case 'email':
          const provider = (fromUser || {}).state.gmail ? 'gmail' : 'microsoft';
          messageType = provider === 'gmail' ? 'GMAIL' : 'OUTLOOK';
          accessToken = (fromUser || {}).state[provider].accessToken;
          refreshToken = (fromUser || {}).state[provider].refreshToken;
          break;
        case 'teams':
          messageType = 'TEAMS';
          accessToken = (fromUser || {}).state.microsoft.accessToken;
          refreshToken = (fromUser || {}).state.microsoft.refreshToken;
          break;
      }
      const signature = ((fromUser || {}).state || {}).emailSignature;
      const emailAddress = demoEmailAddressMap[fromUser.emailAddress] || fromUser.emailAddress;
      const randomCollectiveGoalProgress = Math.round(Math.random() * 100);

      const globalJobVariables = (jobs || []).reduce((r, c) => ({
        ...r,
        [`JOB_${c.id}_TITLE`]: (c || {}).title,
        [`JOB_${c.id}_DESCRIPTION`]: (c || {}).excerpt,
        [`JOB_${c.id}_SHARE_TEXT`]: `Work with me as a ${(job || {}).title}`,
      }), {});
      const globalContentShareVariables = (contentShares || []).reduce((r, c) => ({
        ...r,
        [`CONTENT_${c.id}_TITLE`]: (c || {}).title,
        [`CONTENT_${c.id}_DESCRIPTION`]: (c || {}).description,
        [`CONTENT_${c.id}_SHARE_TEXT`]: 'We are hiring. Apply to one of our openings!',
      }), {});

      const contentShareIDs = Array.isArray(contentShares) ? contentShares.map(({ id }) => id) : [];
      const jobIDs = Array.isArray(jobs) ? jobs.map(({ id }) => id) : [];
      const IDs = [ ...contentShareIDs, ...jobIDs ];

      const input = {
        messageType,
        messageDetails: {
          body: signature ? body.replace('<!-- %%SIGNATURE%% -->', signature) : body,
          subject,
          from: {
            name: (fromUser || {}).fullName,
            emailAddress,
            accessToken,
            refreshToken
          }
        },
        recipients: {
          type: this.isIndividual ? 'INDIVIDUAL' : 'GROUP',
          users: recipients.map(el => {
            console.log({ users, el });
            const mappedUser = users.find(({ emailAddress }) => emailAddress === el.emailAddress);
            if (!mappedUser) {
              this.$notification({
                message: `User ${el.emailAddress} can not be found in the system. The message will not be sent to this user.`,
                variant: 'warning'
              });
              return null;
            }
            const mappedJobs = Array.isArray((mappedUser || {}).jobs) ? mappedUser.jobs : null;
            const mappedContentShares = Array.isArray((mappedUser || {}).contentShares) ? mappedUser.contentShares : null;
            const [ mappedJob ] = mappedJobs || [];
            const [ mappedContentShare ] = mappedContentShares || [];
            const { origin } = this.$root;
            const baseURL = origin.includes('localhost') ? 'https://referrals.reallinks.dev' : origin;
            const contentShareURL = mappedContentShare ? `${baseURL}/c/${mappedContentShare.shareCode}${el.trackingCode ? `?utm_campaign=real-links&utm_source=${el.trackingCode}` : ''}` : null;
            const jobShareURL = mappedJob ? `${baseURL}/r/${mappedJob.shareCode}${el.trackingCode ? `?utm_campaign=real-links&utm_source=${el.trackingCode}` : ''}` : null;
            const jobVariables = (mappedJobs || []).reduce((r, j) => {
              const shareURL = j ? `${baseURL}/r/${j.shareCode}${el.trackingCode ? `?utm_campaign=real-links&utm_source=${el.trackingCode}` : ''}` : null;
              return {
                ...r,
                [`JOB_${j.id}_SHARE_LINK`]: shareURL,
                [`JOB_${j.id}_MATCH_LINK`]: j ? j.linkedInSearchURL.replace('www.', '') : null,
                [`JOB_${j.id}_LINKEDIN_SHARE_LINK`]: shareURL ? getShareURL(this.$i18n, { channel: 'linkedin', company, shareText: jobShareText, shareUrl: shareURL }).replace('www.', '') : null,
                [`JOB_${j.id}_FACEBOOK_SHARE_LINK`]: shareURL ? getShareURL(this.$i18n, { channel: 'facebook', company, shareText: jobShareText, shareUrl: shareURL }).replace('www.', '') : null,
                [`JOB_${j.id}_WHATSAPP_SHARE_LINK`]: shareURL ? getShareURL(this.$i18n, { channel: 'whatsapp', company, shareText: jobShareText, shareUrl: shareURL }).replace('www.', '') : null,
                [`JOB_${j.id}_TWITTER_SHARE_LINK`]: shareURL ? getShareURL(this.$i18n, { channel: 'twitter', company, shareText: jobShareText, shareUrl: shareURL }).replace('www.', '') : null,
                [`JOB_${j.id}_MESSENGER_SHARE_LINK`]: shareURL ? getShareURL(this.$i18n, { channel: 'fb-messenger', company, shareText: jobShareText, shareUrl: shareURL }).replace('www.', '') : null,
              };
            }, {});
            const contentShareVariables = (mappedContentShares || []).reduce((r, c) => {
              const shareURL = c ? `${baseURL}/c/${c.shareCode}${el.trackingCode ? `?utm_campaign=real-links&utm_source=${el.trackingCode}` : ''}` : null;
              return {
                ...r,
                [`CONTENT_${c.id}_SHARE_LINK`]: shareURL,
                [`CONTENT_${c.id}_LINKEDIN_SHARE_LINK`]: shareURL ? getShareURL(this.$i18n, { channel: 'linkedin', company, shareText: contentShareText, shareUrl: shareURL }).replace('www.', '') : null,
                [`CONTENT_${c.id}_FACEBOOK_SHARE_LINK`]: shareURL ? getShareURL(this.$i18n, { channel: 'facebook', company, shareText: contentShareText, shareUrl: shareURL }).replace('www.', '') : null,
                [`CONTENT_${c.id}_WHATSAPP_SHARE_LINK`]: shareURL ? getShareURL(this.$i18n, { channel: 'whatsapp', company, shareText: contentShareText, shareUrl: shareURL }).replace('www.', '') : null,
                [`CONTENT_${c.id}_TWITTER_SHARE_LINK`]: shareURL ? getShareURL(this.$i18n, { channel: 'twitter', company, shareText: contentShareText, shareUrl: shareURL }).replace('www.', '') : null,
                [`CONTENT_${c.id}_MESSENGER_SHARE_LINK`]: shareURL ? getShareURL(this.$i18n, { channel: 'fb-messenger', company, shareText: contentShareText, shareUrl: shareURL }).replace('www.', '') : null,
              };
            }, {});
            return {
              type: 'INDIVIDUAL',
              emailAddress: el.emailAddress,
              variables: {
                ...el.variables,
                ...jobVariables,
                ...contentShareVariables,
                JOB_DESCRIPTION: (mappedJob || {}).excerpt,
                JOB_SHARE_LINK: jobShareURL,
                JOB_MATCH_LINK: mappedJob ? mappedJob.linkedInSearchURL.replace('www.', '') : null,
                CONTENT_SHARE_LINK: contentShareURL,
                CONTENT_LINKEDIN_SHARE_LINK: contentShareURL ? getShareURL(this.$i18n, { channel: 'linkedin', company, shareText: contentShareText, shareUrl: contentShareURL }).replace('www.', '') : null,
                CONTENT_FACEBOOK_SHARE_LINK: contentShareURL ? getShareURL(this.$i18n, { channel: 'facebook', company, shareText: contentShareText, shareUrl: contentShareURL }).replace('www.', '') : null,
                CONTENT_WHATSAPP_SHARE_LINK: contentShareURL ? getShareURL(this.$i18n, { channel: 'whatsapp', company, shareText: contentShareText, shareUrl: contentShareURL }).replace('www.', '') : null,
                CONTENT_TWITTER_SHARE_LINK: contentShareURL ? getShareURL(this.$i18n, { channel: 'twitter', company, shareText: contentShareText, shareUrl: contentShareURL }).replace('www.', '') : null,
                CONTENT_MESSENGER_SHARE_LINK: contentShareURL ? getShareURL(this.$i18n, { channel: 'fb-messenger', company, shareText: contentShareText, shareUrl: contentShareURL }).replace('www.', '') : null,
                JOB_LINKEDIN_SHARE_LINK: jobShareURL ? getShareURL(this.$i18n, { channel: 'linkedin', company, shareText: jobShareText, shareUrl: jobShareURL }).replace('www.', '') : null,
                JOB_FACEBOOK_SHARE_LINK: jobShareURL ? getShareURL(this.$i18n, { channel: 'facebook', company, shareText: jobShareText, shareUrl: jobShareURL }).replace('www.', '') : null,
                JOB_WHATSAPP_SHARE_LINK: jobShareURL ? getShareURL(this.$i18n, { channel: 'whatsapp', company, shareText: jobShareText, shareUrl: jobShareURL }).replace('www.', '') : null,
                JOB_TWITTER_SHARE_LINK: jobShareURL ? getShareURL(this.$i18n, { channel: 'twitter', company, shareText: jobShareText, shareUrl: jobShareURL }).replace('www.', '') : null,
                JOB_MESSENGER_SHARE_LINK: jobShareURL ? getShareURL(this.$i18n, { channel: 'fb-messenger', company, shareText: jobShareText, shareUrl: jobShareURL }).replace('www.', '') : null,
              }
            };
          }).filter(el => el !== null)
        },
        variables: {
          ...(globalJobVariables || {}),
          ...(globalContentShareVariables || {}),
          JOB_TITLE: (job || {}).title,
          JOB_SHARE_TEXT: `Work with me as a ${(job || {}).title}`,
          JOB_MATCH_LINK: (job || {}).linkedInSearchURL,
          CONTENT_TITLE: (contentShare || {}).title,
          CONTENT_DESCRIPTION: (contentShare || {}).description,
          CONTENT_SHARE_TEXT: 'We are hiring. Apply to one of our openings!',
          LEADERBOARD_FIRST_POINTS: 1356,
          LEADERBOARD_SECOND_POINTS: 1110,
          LEADERBOARD_THIRD_POINTS: 936,
          LEADERBOARD_FIRST_NAME: 'John Doe',
          LEADERBOARD_SECOND_NAME: 'Mary Doe',
          LEADERBOARD_THIRD_NAME: 'Steve Doe',
          LEADERBOARD_FIRST_PRIZE_TITLE: '£1000 Amazon Voucher',
          LEADERBOARD_SECOND_PRIZE_TITLE: '£500 Amazon Voucher',
          LEADERBOARD_THIRD_PRIZE_TITLE: '£200 Amazon Voucher',
          PLATFORM_LINK: origin.includes('localhost') ? 'https://referrals.reallinks.dev' : origin,
          COLLECTIVE_GOAL_HEADLINE: (collectiveGoal || {}).headline || ' - ',
          COLLECTIVE_GOAL_PROGRESS: randomCollectiveGoalProgress,
          COLLECTIVE_GOAL_ICON: (collectiveGoal || {}).icon || '',
          COLLECTIVE_GOAL_FIRST_LINE: ((collectiveGoal || {}).display || {}).firstLine || ' - ',
          COLLECTIVE_GOAL_SECOND_LINE: ((collectiveGoal || {}).display || {}).secondLine || ' - ',
          COLLECTIVE_GOAL_PERCENTAGE: randomCollectiveGoalProgress,
          COLLECTIVE_GOAL_PERCENTAGE_REMINDER: 100 - randomCollectiveGoalProgress,
          JOB_LINKEDIN_SCHEDULE_LINK: `${origin.includes('localhost') ? 'https://referrals.reallinks.dev' : origin}/campaign-scheduler/jobs/${IDs.join(',')}`,
          CONTENT_LINKEDIN_SCHEDULE_LINK: `${origin.includes('localhost') ? 'https://referrals.reallinks.dev' : origin}/campaign-scheduler/content/${IDs.join(',')}`,
        }
      };
      console.log({ input });
      try {
        await this.$apollo.mutate({
          mutation: SendTestMessageMutation,
          variables: { input: JSON.stringify(input) },
        });
      } catch (error) {
        this.$notifyErr(error);
        console.log(error);
      }
    },
  }
};
</script>



// WEBPACK FOOTER //
// src/components/campaign-admin/test-message-form.vue